import { useRef, useState } from "react";
import { Play, Volume2, VolumeX } from "lucide-react";
import AnimateOnScroll from "../AnimateOnScroll";

interface VideoFeatureSectionProps {
  heading: string;
  body: string;
  eyebrow?: string;
  sources: { src: string; type: string }[];
  poster: string;
  reverse?: boolean;
}

const VideoFeatureSection = ({
  heading,
  body,
  eyebrow,
  sources,
  poster,
  reverse = false,
}: VideoFeatureSectionProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(false);

  const handlePlay = () => {
    setStarted(true);
    // play after render
    requestAnimationFrame(() => {
      const v = videoRef.current;
      if (!v) return;
      v.muted = muted;
      v.play().catch(() => {
        // fallback: try muted
        v.muted = true;
        setMuted(true);
        v.play().catch(() => {});
      });
    });
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    const next = !muted;
    setMuted(next);
    video.muted = next;
  };

  return (
    <section className="mx-auto max-w-6xl px-6 py-12 md:py-16">
      <div
        className={`grid gap-10 md:grid-cols-2 md:items-center ${
          reverse ? "md:[&>*:first-child]:order-2" : ""
        }`}
      >
        <AnimateOnScroll>
          <div className="relative mx-auto w-full max-w-sm overflow-hidden rounded-3xl bg-muted shadow-lg">
            {!started ? (
              <button
                type="button"
                onClick={handlePlay}
                aria-label="Spill av video"
                className="group relative block w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <img
                  src={poster}
                  alt=""
                  aria-hidden="true"
                  className="aspect-[9/16] w-full object-cover"
                  loading="lazy"
                  width={720}
                  height={1280}
                />
                <span className="absolute inset-0 flex items-center justify-center bg-foreground/20 transition-colors group-hover:bg-foreground/30">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background/90 shadow-lg transition-transform group-hover:scale-110">
                    <Play className="ml-1 h-7 w-7 fill-foreground text-foreground" aria-hidden="true" />
                  </span>
                </span>
              </button>
            ) : (
              <>
                <video
                  ref={videoRef}
                  className="aspect-[9/16] w-full object-cover"
                  poster={poster}
                  controls
                  playsInline
                  preload="metadata"
                  controlsList="nodownload noremoteplayback"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  {sources.map((s) => (
                    <source key={s.src} src={s.src} type={s.type} />
                  ))}
                </video>
                <button
                  type="button"
                  onClick={toggleMute}
                  aria-label={muted ? "Skru på lyd" : "Skru av lyd"}
                  aria-pressed={!muted}
                  className="absolute bottom-16 right-4 flex h-11 w-11 items-center justify-center rounded-full bg-foreground/60 text-background shadow-lg backdrop-blur-sm transition-all hover:bg-foreground/80 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  {muted ? (
                    <VolumeX className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <Volume2 className="h-5 w-5" aria-hidden="true" />
                  )}
                </button>
              </>
            )}
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll delay="delay-200">
          <div>
            {eyebrow && (
              <p className="font-body text-sm font-semibold uppercase tracking-wider text-primary">
                {eyebrow}
              </p>
            )}
            <h2 className="mt-2 font-heading text-3xl font-bold md:text-4xl">
              {heading}
            </h2>
            {body.split("\n\n").map((p, i) => (
              <p
                key={i}
                className="mt-4 font-body text-lg leading-relaxed text-muted-foreground"
              >
                {p}
              </p>
            ))}
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default VideoFeatureSection;
